import type { CinematicSequence } from './cinematic-sequence.js';
import type { UnitSystem } from "./unit-system.js";
import type { VideoPresentation } from "./video-presentation.js";
import { VIDEO_SAFETY_SECONDS } from "./service-policy.js";
import type { TrackAppearance } from './track-appearance.js';
import type { DepthOfFieldSettings } from './depth-of-field.js';
import { CAMERA_PLATFORM_IDS, normalizeCameraPlatform, type CameraPlatformId } from './camera-platform.js';
export { CAMERA_PLATFORM_IDS, type CameraPlatformId };
/** Compatibility names for integrations using the earlier camera terminology. */
export { CAMERA_PLATFORM_IDS as CAMERA_STYLE_IDS, type CameraPlatformId as CameraStyleId };

/** Supported encoded-video presentation names. */
export type RenderPresetId = "landscape-16x9" | "social-4x5" | "vertical-9x16";

/** Camera-distance identifiers shared with the composition. */
export const CAMERA_DISTANCE_IDS = ["wide", "standard", "close", "x-close", "xx-close", "telephoto-800"] as const;

/** Supported distance between the moving camera and route subject. */
export type CameraDistanceId = (typeof CAMERA_DISTANCE_IDS)[number];

/** Supported route appearances shared with the composition. */
export const ROUTE_LINE_STYLE_IDS = ["standard", "pipe"] as const;
export type RouteLineStyleId = (typeof ROUTE_LINE_STYLE_IDS)[number];

/** Smallest complete film accepted by the render pipeline. */
export const MIN_RENDER_DURATION_SECONDS = 5;

/** Largest complete film accepted to bound frame count and encoder cost. */
export const MAX_RENDER_DURATION_SECONDS = VIDEO_SAFETY_SECONDS;

/** Fully derived output settings accepted by the encoder. */
export interface RenderOutput {
  readonly placeTitle?: import('./place-title.js').PlaceTitle | undefined;
  readonly exportRange?: import('./export-range.js').ExportRange | undefined;
  readonly cinematicSequence?: CinematicSequence | undefined;
  readonly depthOfField?: DepthOfFieldSettings | undefined;
  readonly cameraAngle?: number | undefined;
  readonly cameraGimbal?: import('./camera-gimbal.js').CameraGimbalId | undefined;
  readonly trackAppearance?: TrackAppearance | undefined;
  readonly presentation?: VideoPresentation | undefined;
  readonly unitSystem?: UnitSystem | undefined;
  readonly terrainExaggeration?: number | undefined;
  readonly hidePlaces?: boolean | undefined;
  readonly mapStyle?: string | undefined;
  readonly lineStyle: RouteLineStyleId;
  readonly cameraDistance: CameraDistanceId;
  /** Camera platform; the serialized field name is retained for saved-job compatibility. */
  readonly cameraStyle: CameraPlatformId;
  readonly cameraStyleVersion: 3 | 4;
  readonly preset: RenderPresetId;
  readonly format: "mp4";
  readonly width: number;
  readonly height: number;
  readonly framesPerSecond: 30;
  readonly durationSeconds: number;
}

const DIMENSIONS: Readonly<Record<RenderPresetId, { width: number; height: number }>> = {
  "landscape-16x9": { width: 1920, height: 1080 },
  "social-4x5": { width: 1080, height: 1350 },
  "vertical-9x16": { width: 1080, height: 1920 },
};

/**
 * Resolves a trusted preset into fixed MP4 output settings.
 *
 * @param preset - Allowlisted presentation identifier.
 * @param durationSeconds - Film duration from five through 240 seconds.
 * @param cameraStyle - Allowlisted camera platform, using the compatible request field.
 * @param cameraDistance - Allowlisted distance from the route subject.
 * @param lineStyle - Allowlisted appearance of the traveled route.
 * @returns Complete deterministic output contract.
 * @throws RangeError when the preset or duration is unsupported.
 */
export function resolveOutput(
  preset: string,
  durationSeconds: number,
  cameraStyle: string,
  cameraDistance: string,
  lineStyle: string = "pipe",
  cameraStyleVersion: 3 | 4 = 4,
): RenderOutput {
  if (!(preset in DIMENSIONS)) {
    throw new RangeError(`Unsupported render preset: ${preset}`);
  }
  if (
    !Number.isInteger(durationSeconds)
    || durationSeconds < MIN_RENDER_DURATION_SECONDS
    || durationSeconds > MAX_RENDER_DURATION_SECONDS
  ) {
    throw new RangeError(`Render duration must be an integer from five through ${MAX_RENDER_DURATION_SECONDS} seconds.`);
  }
  const platform = normalizeCameraPlatform(cameraStyle);
  if (!platform) {
    throw new RangeError(`Unsupported camera platform: ${cameraStyle}`);
  }
  if (!CAMERA_DISTANCE_IDS.some((id) => id === cameraDistance)) {
    throw new RangeError(`Unsupported camera distance: ${cameraDistance}`);
  }
  if (!ROUTE_LINE_STYLE_IDS.some((id) => id === lineStyle)) {
    throw new RangeError(`Unsupported line style: ${lineStyle}`);
  }
  const id = preset as RenderPresetId;
  const dimensions = DIMENSIONS[id];
  return {
    lineStyle: lineStyle as RouteLineStyleId,
    cameraDistance: cameraDistance as CameraDistanceId,
    cameraStyle: platform,
    cameraStyleVersion,
    durationSeconds,
    format: "mp4",
    framesPerSecond: 30,
    height: dimensions.height,
    preset: id,
    width: dimensions.width,
  };
}
